/**
 * AppDock — mobile-app-style fixed bottom dock.
 *
 * Behaviour:
 *   · Anchors at the bottom of the viewport on **every screen size** (safe-area padded).
 *   · Slot 1 (leftmost) is always **Home**.
 *   · Slots 2-6 are the **5 most-recently-used routes** (per-user for signed-in
 *     users via `/personal/recent`, per-device localStorage `f2n_recent_dock`
 *     for guests).
 *   · Slot 7 is **More**, opens a full-screen grid drawer with the full app
 *     manifest so the user can reach anything the MRU dock hasn't surfaced.
 *
 * The dock listens to every route change: it bumps the current path into the
 * MRU list so the user always sees their live behaviour reflected. Route
 * metadata (label, icon, colour) comes from `ROUTE_META` — a curated
 * manifest that maps the first segment of a URL to a pretty label. Routes
 * not in the manifest fall back to a generic 🧭 Compass icon with a
 * humanised path label ("Live-Trains" from "/live-trains").
 */
import { ROUTE_SEGMENTS, ROUTE_PATHS } from "@/lib/routeSegments";
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Home as House, Compass, Search, MapPin, Layers, Car, Building2, Ticket, Plane,
  Heart, Sparkles, Package, ShoppingBag, Bell, MessageCircle, Lock, Coins, Newspaper,
  Wallet, ChevronsUpDown, Users, Briefcase, GraduationCap, X, LayoutGrid, User,
  Star, Cpu, Landmark, Camera, Video, Utensils, HeartPulse, Zap, Flame, Award,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { LocaleButton } from "@/components/LocalePicker";

/* ── Route manifest ─────────────────────────────────────────────────────── */
const ROUTE_META = {
  "":              { label: "Home",       icon: House,        color: "text-emerald-700" },
  "nearby":        { label: "Nearby",     icon: MapPin,       color: "text-emerald-700" },
  "map":           { label: "Live Map",   icon: Layers,       color: "text-sky-700" },
  "search":        { label: "Search",     icon: Search,       color: "text-stone-700" },
  "life":          { label: "Life",       icon: Sparkles,     color: "text-fuchsia-700" },
  "account":       { label: "Account",    icon: User,         color: "text-stone-700" },
  "rideshare":     { label: "RideShare",  icon: Car,          color: "text-emerald-700" },
  "carpool":       { label: "Carpool",    icon: Car,          color: "text-emerald-700" },
  "property":      { label: "Property",   icon: Building2,    color: "text-orange-700" },
  "services":      { label: "Services",   icon: Briefcase,    color: "text-sky-700" },
  "charity":       { label: "Charity",    icon: Heart,        color: "text-rose-600" },
  "donate":        { label: "Donate",     icon: Heart,        color: "text-rose-600" },
  "astrology":     { label: "Astrology",  icon: Star,         color: "text-purple-700" },
  "flights":       { label: "Flights",    icon: Plane,        color: "text-sky-700" },
  "movies":        { label: "Movies",     icon: Video,        color: "text-red-700" },
  "food":          { label: "Food",       icon: Utensils,     color: "text-orange-700" },
  "products":      { label: "Products",      icon: ShoppingBag,  color: "text-emerald-700" },
  "cart":          { label: "Cart",       icon: ShoppingBag,  color: "text-emerald-700" },
  "orders":        { label: "Orders",     icon: Package,      color: "text-stone-700" },
  "shop":          { label: "Shop",       icon: ShoppingBag,  color: "text-emerald-700" },
  "need":          { label: "Find what I need", icon: Sparkles, color: "text-amber-700" },
  "wishlist":      { label: "Wishlist",   icon: Heart,        color: "text-rose-600" },
  "saved":         { label: "Saved",      icon: Heart,        color: "text-rose-600" },
  "locker":        { label: "Locker",     icon: Lock,         color: "text-stone-700" },
  "wallet":        { label: "Wallet",     icon: Wallet,       color: "text-emerald-700" },
  "findpay":       { label: "FindPay",    icon: Coins,        color: "text-emerald-700" },
  "pay":           { label: "Pay",        icon: Coins,        color: "text-emerald-700" },
  "messenger":     { label: "Messages",   icon: MessageCircle,color: "text-emerald-700" },
  "messages":      { label: "Messages",   icon: MessageCircle,color: "text-emerald-700" },
  "notices":       { label: "Notices",    icon: Newspaper,    color: "text-stone-700" },
  "alerts":        { label: "Alerts",     icon: Bell,         color: "text-amber-700" },
  "notifications": { label: "Alerts",     icon: Bell,         color: "text-amber-700" },
  "inbox":         { label: "Inbox",      icon: Bell,         color: "text-amber-700" },
  "health":        { label: "Health",     icon: HeartPulse,   color: "text-rose-600" },
  "jobs":          { label: "Jobs",       icon: Briefcase,    color: "text-sky-700" },
  "schools":       { label: "Schools",    icon: GraduationCap,color: "text-indigo-700" },
  "matrimonial":   { label: "Matches",    icon: Users,        color: "text-rose-600" },
  "dating":        { label: "Dating",     icon: Users,        color: "text-rose-600" },
  "visa":          { label: "Visa",       icon: Landmark,     color: "text-emerald-700" },
  "camps":         { label: "Camps",      icon: Flame,        color: "text-amber-700" },
  "clubs":         { label: "Clubs",      icon: Award,        color: "text-purple-700" },
  "reels":         { label: "Reels",      icon: Video,        color: "text-fuchsia-700" },
  "feed":          { label: "Feed",       icon: Newspaper,    color: "text-stone-700" },
  "priority-card": { label: "Priority",   icon: Ticket,       color: "text-purple-700" },
  "explore-18":    { label: "18+",        icon: Ticket,       color: "text-rose-700" },
  "welfare":       { label: "Welfare",    icon: Heart,        color: "text-emerald-700" },
  "volunteer":     { label: "Volunteer",  icon: Heart,        color: "text-emerald-700" },
  "foundation":    { label: "Foundation", icon: Heart,        color: "text-emerald-700" },
  "learn":         { label: "Learn",      icon: GraduationCap,color: "text-indigo-700" },
  "lab":           { label: "AI Lab",     icon: Cpu,          color: "text-sky-700" },
  "ai":            { label: "AI",         icon: Sparkles,     color: "text-purple-700" },
  "hub":           { label: "Hub",        icon: LayoutGrid,   color: "text-stone-700" },
  "tools":         { label: "Tools",      icon: Cpu,          color: "text-sky-700" },
  "car-services":  { label: "Car Care",   icon: Car,          color: "text-orange-700" },
  "dealers":       { label: "Dealers",    icon: Building2,    color: "text-orange-700" },
  "brands":        { label: "Brands",     icon: Star,         color: "text-purple-700" },
  "clubs":         { label: "Clubs",      icon: Award,        color: "text-purple-700" },
  "emergency":     { label: "SOS",        icon: Flame,        color: "text-rose-600" },
  "washrooms":     { label: "Washrooms",  icon: MapPin,       color: "text-sky-700" },
  "post":          { label: "Post",       icon: Camera,       color: "text-emerald-700" },
  "dashboard":     { label: "Dashboard",  icon: LayoutGrid,   color: "text-stone-700" },
  "me":            { label: "Me",         icon: User,         color: "text-stone-700" },
  "settings":      { label: "Settings",   icon: LayoutGrid,   color: "text-stone-700" },
  "listings":      { label: "Listings",   icon: MapPin,       color: "text-emerald-700" },
  "hotel":         { label: "Hotels",     icon: Building2,    color: "text-sky-700" },
  "food":          { label: "Food",       icon: Utensils,     color: "text-orange-700" },
  "delivery":      { label: "Delivery",   icon: Package,      color: "text-emerald-700" },
  "parcels":       { label: "Parcels",    icon: Package,      color: "text-emerald-700" },
  "electricity":   { label: "Electric",   icon: Zap,          color: "text-amber-700" },
};

const DEFAULT_GUEST_DOCK = ["", "nearby", "rideshare", "search", "charity"];

/** Turn a pathname into a **root module key** — first non-empty segment.
 * Root ("/") → "". Nested paths ("/rideshare/123") → "rideshare".
 * Routes we never want to track in the dock (auth/callback etc.) return null.
 */
function keyOf(path) {
  if (!path || path === "/") return "";
  const seg = (path.split("/").filter(Boolean)[0] || "").toLowerCase();
  const skip = new Set(["auth", "r"]);   // /r/<token> is the public share page
  // Unknown / 404 / retired paths never become dock destinations.
  if (skip.has(seg) || !ROUTE_SEGMENTS.has(seg)) return null;
  return seg;
}

function metaOf(key) {
  const m = ROUTE_META[key];
  if (m) return m;
  const label = key ? key.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase()) : "Home";
  return { label, icon: Compass, color: "text-stone-700" };
}

const GUEST_LS_KEY = "f2n_recent_dock";
function readGuestMRU() {
  try { return JSON.parse(localStorage.getItem(GUEST_LS_KEY) || "[]"); }
  catch { return []; }
}
function writeGuestMRU(list) {
  try { localStorage.setItem(GUEST_LS_KEY, JSON.stringify(list.slice(0, 10))); }
  catch { /* noop */ }
}

/** Bumps a key to the front, dedup, keep at most 10 (dock shows 5 slots + buffer). */
function bumpMRU(list, key) {
  const filtered = list.filter(k => k !== key);
  return [key, ...filtered].slice(0, 10);
}

export default function AppDock() {
  const { user } = useAuth();
  const loc = useLocation();
  const [mru, setMru] = useState(() => readGuestMRU());
  const [moreOpen, setMoreOpen] = useState(false);
  const { t } = useI18n();

  /* ── Hydrate MRU from backend for signed-in users. */
  const hydrate = useCallback(async () => {
    if (!user) { setMru(readGuestMRU()); return; }
    try {
      const { data } = await api.get("/personal/recent");
      const keys = (Array.isArray(data) ? data : []).map(r => r.module);
      setMru(keys);
    } catch { /* silent */ }
  }, [user]);
  useEffect(() => { hydrate(); }, [hydrate]);

  /* ── Track current route on every navigation. */
  useEffect(() => {
    const k = keyOf(loc.pathname);
    if (k === null) return;
    // Update local state immediately (works for guests + gives auth users
    // instant feedback while the backend upsert is in flight).
    setMru(prev => {
      const next = bumpMRU(prev, k);
      if (!user) writeGuestMRU(next);
      return next;
    });
    if (user) {
      const m = metaOf(k);
      api.post("/personal/track", { module: k || "home", label: m.label }).catch(() => {});
    }
  }, [loc.pathname, user]);

  /* ── Build the 7-slot dock: Home + 5 MRU (excluding home) + More. */
  const currentKey = keyOf(loc.pathname);
  const dockKeys = useMemo(() => {
    const seed = mru.length ? mru : DEFAULT_GUEST_DOCK;
    // Home is pinned leftmost; drop it from MRU to avoid dupes; keep 5 MRU max.
    const seen = new Set();
    const rest = seed.filter(k => {
      if (k === "" || k === "home" || !ROUTE_SEGMENTS.has(k)) return false;
      if (k.startsWith("admin") && !user?.is_admin) return false;   // never surface admin routes to non-admins
      const label = metaOf(k).label;                                   // e.g. messages + messenger → one slot
      if (seen.has(label)) return false;
      seen.add(label);
      return true;
    }).slice(0, 5);
    return ["", ...rest];
  }, [mru, user]);

  return (
    <>
      <nav data-testid="app-dock"
           className="fixed bottom-0 inset-x-0 bg-white/95 backdrop-blur border-t border-stone-200 shadow-[0_-2px_20px_rgba(0,0,0,0.05)]"
           style={{ paddingBottom: "var(--f2n-safe-bottom)", zIndex: "var(--z-mobile-nav)" }}>
        <div className="max-w-3xl mx-auto grid items-stretch"
             style={{ gridTemplateColumns: `repeat(${dockKeys.length + 1}, minmax(0, 1fr))` }}>
          {dockKeys.map((k, i) => {
            const m = metaOf(k);
            const active = currentKey === k || (currentKey === null && k === "");
            const to = k === "" ? "/" : `/${k}`;
            return (
              <Link key={`${k}-${i}`} to={to}
                    className={`flex flex-col items-center justify-center py-2 gap-0.5 min-w-0 text-[10px] transition-colors ${active ? "text-emerald-700" : "text-stone-500 hover:text-stone-800"}`}
                    data-testid={`dock-slot-${k || "home"}`}>
                <m.icon size={20} strokeWidth={active ? 2.4 : 1.9}/>
                <span className="truncate w-full text-center">{t(`dock.${k || "home"}`, m.label)}</span>
              </Link>
            );
          })}
          <button onClick={() => setMoreOpen(true)}
                  className={`flex flex-col items-center justify-center py-2 gap-0.5 min-w-0 text-[10px] text-stone-500 hover:text-stone-800 ${moreOpen ? "text-emerald-700" : ""}`}
                  data-testid="dock-slot-more">
            <LayoutGrid size={20} strokeWidth={1.9}/>
            <span>{t("shell.more")}</span>
          </button>
        </div>
      </nav>
      {/* Padding-spacer so the fixed dock never covers page content. */}
      <div aria-hidden style={{ height: "calc(68px + var(--f2n-safe-bottom))" }}/>
      {moreOpen && <MoreDrawer onClose={() => setMoreOpen(false)}/>}
    </>
  );
}


/* ── More drawer — grid of every app in the manifest ────────────────────── */
function MoreDrawer({ onClose }) {
  const { t } = useI18n();
  const entries = useMemo(() => {
    const seen = new Set();
    const list = [];
    for (const [key, meta] of Object.entries(ROUTE_META)) {
      if (key !== "" && !ROUTE_PATHS.has(`/${key}`)) continue;   // 404 / redirect-only destinations
      const label = meta.label;
      if (seen.has(label)) continue;             // dedup by label (e.g. rideshare + carpool)
      seen.add(label);
      list.push({ key, ...meta, to: key === "" ? "/" : `/${key}` });
    }
    return list.sort((a, b) => a.label.localeCompare(b.label));
  }, []);
  return (
    <div className="fixed inset-0 z-[80] bg-white overflow-y-auto" data-testid="app-dock-more-drawer">
      <div className="sticky top-0 bg-white border-b border-stone-200 flex items-center justify-between px-4 py-3 z-10">
        <div className="font-black text-stone-900 text-lg">{t("shell.all_apps")}</div>
        <button onClick={onClose} className="p-2 text-stone-500 hover:text-stone-800 rounded-full hover:bg-stone-100"
                data-testid="app-dock-more-close" aria-label={t("shell.close")}>
          <X size={20}/>
        </button>
      </div>
      <div className="p-4 grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-3">
        {entries.map(e => (
          <Link key={e.key} to={e.to} onClick={onClose}
                className="flex flex-col items-center gap-1 p-3 rounded-2xl border border-stone-200 hover:border-stone-300 hover:bg-stone-50 text-center"
                data-testid={`app-dock-more-${e.key || "home"}`}>
            <div className={`w-11 h-11 rounded-full bg-stone-50 grid place-items-center ${e.color}`}>
              <e.icon size={22}/>
            </div>
            <div className="text-[11px] font-semibold text-stone-800 leading-tight">{t(`dock.${e.key || "home"}`, e.label)}</div>
          </Link>
        ))}
      </div>
      <div className="sticky bottom-0 bg-white/95 backdrop-blur border-t border-stone-200 p-3 flex justify-center"
           style={{ paddingBottom: "calc(12px + var(--f2n-safe-bottom))" }}>
        <LocaleButton className="h-11 px-5 text-[13px]" testid="dock-locale-btn" />
      </div>
    </div>
  );
}
